import { useEffect, useRef, useState } from 'react';
import { backendClient } from '@/lib/api-client';
import type { Task } from '@/components/ui/columns-tasks';

export interface OutlookCalendar {
  id: string;
  name: string;
  isDefaultCalendar: boolean;
  canEdit: boolean;
  color?: string;
  calendarGroupName?: string;
  owner?: { address?: string; name?: string };
}

interface CalendarsResponse {
  calendars?: OutlookCalendar[];
  permission_granted?: boolean;
}

const POLL_INTERVAL_MS = 120_000;
const BUSY_RETRY_MS = 5_000;

/**
 * Resolve Graph calendar IDs on each task's sync records to this user's IDs.
 * Shared calendars have a different Graph ID per user; the backend stores the
 * writer's ID and `owner_address`, which we match against our calendar list.
 */
export function remapTaskCalendars(tasks: Task[], calendars: OutlookCalendar[]): Task[] {
  if (calendars.length === 0) return tasks;

  const myCalIds = new Set(calendars.map((c) => c.id));
  const ownerToMyCalId = new Map<string, string>();
  calendars.forEach((cal) => {
    const addr = cal.owner?.address?.toLowerCase();
    if (addr) ownerToMyCalId.set(addr, cal.id);
  });

  return tasks.map((task) => ({
    ...task,
    synced_calendars: (task.synced_calendars || []).map((sc) => {
      const ownerKey = sc.owner_address?.toLowerCase();
      const resolved = myCalIds.has(sc.calendar_id)
        ? sc.calendar_id
        : (ownerKey && ownerToMyCalId.get(ownerKey)) || sc.calendar_id;
      return {
        ...sc,
        original_calendar_id: sc.calendar_id,
        calendar_id: resolved,
      };
    }),
  }));
}

export function useOutlookCalendars() {
  const [calendars, setCalendars] = useState<OutlookCalendar[]>([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const hasCalendarsRef = useRef(false);
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let busyRetryId: ReturnType<typeof setTimeout> | undefined;

    const fetchCalendars = async () => {
      if (busyRetryId !== undefined) {
        clearTimeout(busyRetryId);
        busyRetryId = undefined;
      }
      if (!hasCalendarsRef.current) {
        setIsLoading(true);
      }

      let stayLoading = false;
      try {
        const response = await backendClient.get<CalendarsResponse>('/deadlines/calendars');
        if (cancelled) return;
        const data = response.data;
        hasResolvedRef.current = true;
        if (data.permission_granted === false) {
          setPermissionGranted(false);
          setCalendars([]);
          hasCalendarsRef.current = false;
          return;
        }
        const next = data.calendars || [];
        setPermissionGranted(true);
        setCalendars(next);
        hasCalendarsRef.current = next.length > 0;
      } catch (error: unknown) {
        // Keep last calendars on failure. 429 = mailbox Graph slot busy.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 429) {
          if (!cancelled && !hasResolvedRef.current) {
            stayLoading = true;
            busyRetryId = setTimeout(() => {
              void fetchCalendars();
            }, BUSY_RETRY_MS);
          }
          return;
        }
        console.error('Error fetching calendars:', error);
      } finally {
        if (!cancelled && !stayLoading) setIsLoading(false);
      }
    };

    fetchCalendars();
    const intervalId = setInterval(fetchCalendars, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (busyRetryId !== undefined) clearTimeout(busyRetryId);
    };
  }, []);

  return { calendars, permissionGranted, isLoading };
}
